import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import CalendarHeatmap from "react-calendar-heatmap";
import "react-calendar-heatmap/dist/styles.css";
import { api } from "../api/api";
import "../style/AlertasPage.css";
import {
    AlertTriangle,
    CalendarDays,
    Satellite,
    Sprout,
    ClipboardList,
    CheckCircle2,
    ArrowDownCircle,
    ArrowUpCircle,
    Hash,
    ChevronDown,
    ChevronUp,
} from "lucide-react";

export default function AlertasPage() {
    const [isOpen, setIsOpen] = useState(false);
    const [alertas, setAlertas] = useState([]);
    const [alertasRaw, setAlertasRaw] = useState([]);
    const [verMas, setVerMas] = useState(false);
    const [terrenos, setTerrenos] = useState([]);
    const [sensores, setSensores] = useState([]);
    const [terrenoId, setTerrenoId] = useState("");
    const [sensorId, setSensorId] = useState("");
    const [loadingTerrenos, setLoadingTerrenos] = useState(true);
    const [loadingSensores, setLoadingSensores] = useState(false);
    const [tooltip, setTooltip] = useState({ visible: false, content: "", x: 0, y: 0 });
    const [headerHeight, setHeaderHeight] = useState(0);
    const [tituloHeight, setTituloHeight] = useState(0);

    const contenidoRef = useRef(null);
    const headerRef = useRef(null);
    const tituloRef = useRef(null);


    const unidadesPorSensor = {
        temperatura: "°C",
        humedad: "%",
        "humedad de suelo": "%",
        luz: "lux",
        ph: "pH",
        "conductividad eléctrica": "µS/cm",
        co2: "ppm",
        "presión atmosférica": "hPa",
        "velocidad del viento": "m/s",
        lluvia: "mm",
    };

    useEffect(() => {
        if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
        if (tituloRef.current) setTituloHeight(tituloRef.current.offsetHeight);
    }, []);

    useEffect(() => {
        setTimeout(() => {
            if (contenidoRef.current) contenidoRef.current.scrollTop = 0;
            else window.scrollTo({ top: 0, behavior: "auto" });
        }, 50);
    }, []);

    const fetchTerrenos = async () => {
        setLoadingTerrenos(true);
        try {
            const res = await api.getTerrenos();
            setTerrenos(res.success ? res.terrenos : []);
        } catch (err) {
            console.error("⚠ Error cargando terrenos:", err);
            setTerrenos([]);
        } finally {
            setLoadingTerrenos(false);
        }
    };

    useEffect(() => { fetchTerrenos(); }, []);

    const fetchSensores = async () => {
        if (!terrenoId) {
            setSensores([]);
            return;
        }
        setLoadingSensores(true);
        try {
            const token = localStorage.getItem("token");
            const res = await fetch("https://agro-nexus-prod.vercel.app/api/usuario/sensores-datos", {
                headers: { Authorization: `Bearer ${token}` },
            });
            const json = await res.json();
            if (!json.success) {
                setSensores([]);
                setLoadingSensores(false);
                return;
            }
            const map = {};
            json.sensores.forEach((r) => {
                const id = r.sensor_id;
                if (!map[id]) map[id] = { ...r };
                else if (r.fecha_hora && new Date(r.fecha_hora) > new Date(map[id].fecha_hora)) map[id] = { ...r };
            });
            const sensoresTerreno = Object.values(map).filter(
                (s) => s.terreno_id === parseInt(terrenoId)
            );
            setSensores(sensoresTerreno);
        } catch (err) {
            console.error("⚠ Error cargando sensores:", err);
            setSensores([]);
        } finally {
            setLoadingSensores(false);
        }
    };

    useEffect(() => {
        fetchSensores();
        setSensorId("");
    }, [terrenoId]);

    const fetchAlertas = async () => {
        try {
            const res = await api.getAlertas(terrenoId, sensorId);
            if (res.success) {

                const alertasPorFecha = {};
                (res.alertas || []).forEach((a) => {
                    const fecha = a.fecha.split("T")[0];
                    if (!alertasPorFecha[fecha]) {
                        alertasPorFecha[fecha] = {
                            date: fecha,
                            count: 0,
                            info: a,
                            todas: []
                        };
                    }
                    alertasPorFecha[fecha].count++;
                    alertasPorFecha[fecha].todas.push(a);
                });

                const parsed = Object.values(alertasPorFecha);
                setAlertas(parsed);
                setAlertasRaw(res.alertas || []);
            } else {
                setAlertas([]);
                setAlertasRaw([]);
            }
        } catch (err) {
            console.error("⚠ Error cargando alertas:", err);
            setAlertas([]);
            setAlertasRaw([]);
        }
    };

    useEffect(() => { fetchAlertas(); }, [terrenoId, sensorId]);

    const alertasOrdenadas = [...alertasRaw].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    const alertasMostradas = verMas ? alertasOrdenadas : alertasOrdenadas.slice(0, 5);

    const obtenerUnidad = (tipo, id) => {
        if (tipo) {
            const key = tipo.toString().toLowerCase();
            return unidadesPorSensor[key] || "";
        }
        const encontrado = sensores.find(
            (s) => Number(s.sensor_id) === Number(id) || Number(s.id) === Number(id)
        );
        if (encontrado?.tipo_sensor) {
            const key = encontrado.tipo_sensor.toLowerCase();
            return unidadesPorSensor[key] || "";
        }
        return "";
    };

    const mostrarTooltip = (event, value) => {
        if (!value || !value.info) return;

        let detalles = "";
        if (value.count > 1) {

            detalles = `📅 ${value.date}\n${value.count} alertas en este día\n\n`;
            value.todas.slice(0, 3).forEach((info, idx) => {
                const unidad = obtenerUnidad(info.tipo_sensor, info.sensor_id);
                detalles += `${idx + 1}. ${info.sensor_nombre || ""} (${info.tipo?.toUpperCase() || ""}): ${info.valor} ${unidad}\n`;
            });
            if (value.count > 3) {
                detalles += `... y ${value.count - 3} más`;
            }
        } else {

            const info = value.info;
            const unidad = obtenerUnidad(info.tipo_sensor, info.sensor_id);
            const fechaLocal = info.fecha ? new Date(info.fecha).toLocaleString("es-ES") : "";
            detalles = `📅 ${fechaLocal}
Sensor: ${info.sensor_nombre || ""}
Tipo alerta: ${info.tipo ? info.tipo.toUpperCase() : ""}
Valor: ${info.valor !== undefined ? info.valor : ""} ${unidad}`;
        }

        setTooltip({
            visible: true,
            content: detalles,
            x: event.clientX + 10,
            y: event.clientY + 10,
        });
    };

    return (
        <div className="contenedor-alertas">
            <div className="contenedor-barra-lateral">
                <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} />
            </div>

            <div className="contenedor-global-alertas">
                {/* Header fijo */}
                <div ref={headerRef} className="header-fijo-alertas">
                    <Header isOpen={isOpen} setIsOpen={setIsOpen} />
                </div>

                {/* Contenido scrollable */}
                <div
                    className="contenedor-principal-alertas"
                    style={{ paddingTop: headerHeight + tituloHeight + 20 }}
                >
                    <div className="zona-contenido-alertas" ref={contenidoRef}>
                        <div className="tarjeta-alertas">
                            <div className="filtros-alertas">
                                <select value={terrenoId} onChange={(e) => setTerrenoId(e.target.value)}>
                                    <option value="">
                                        <Sprout size={16} /> Selecciona un terreno
                                    </option>
                                    {loadingTerrenos ? (
                                        <option disabled>Cargando...</option>
                                    ) : (
                                        terrenos.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {t.nombre} ({t.ubicacion || "Sin ubicación"})
                                            </option>
                                        ))
                                    )}
                                </select>

                                <select
                                    value={sensorId}
                                    onChange={(e) => setSensorId(e.target.value)}
                                    disabled={!terrenoId}
                                >
                                    <option value="">
                                        <Satellite size={16} /> Todos los sensores
                                    </option>
                                    {loadingSensores ? (
                                        <option disabled>Cargando sensores...</option>
                                    ) : (
                                        sensores.map((s) => (
                                            <option key={s.sensor_id || s.id} value={s.sensor_id || s.id}>
                                                {s.sensor || s.nombre} ({s.tipo_sensor || "—"})
                                            </option>
                                        ))
                                    )}
                                </select>
                            </div>

                            <div className="calendario-alertas" onMouseLeave={() => setTooltip({ ...tooltip, visible: false })}>
                                <CalendarHeatmap
                                    startDate={new Date(new Date().setFullYear(new Date().getFullYear() - 1))}
                                    endDate={new Date()}
                                    values={alertas}
                                    className="calendario-matriz"
                                    gutterSize={6}
                                    showWeekdayLabels
                                    weekdayLabels={["D", "L", "M", "X", "J", "V", "S"]}
                                    monthLabels={["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"]}
                                    classForValue={(value) => {
                                        if (!value || !value.info) return "color-vacio";
                                        if (value.count > 1) return "color-multiple";
                                        return value.info && value.info.tipo === "alto" ? "color-alto" : "color-bajo";
                                    }}
                                    onMouseOver={mostrarTooltip}
                                    tooltipDataAttrs={(value) => {
                                        return {
                                            'data-tip': value.date ? `Alertas: ${value.count || 0}` : 'Sin alertas',
                                        };
                                    }}
                                />
                                {tooltip.visible && (
                                    <div className="tooltip-alertas" style={{ top: tooltip.y, left: tooltip.x }}>
                                        {tooltip.content.split("\n").map((linea, i) => (
                                            <div key={i}>{linea}</div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="resumen-alertas">
                                <h3><ClipboardList size={18} /> Resumen de Alertas</h3>
                                {alertasMostradas.length === 0 ? (
                                    <p className="sin-datos-alertas"><CheckCircle2 size={18} /> No hay alertas registradas</p>
                                ) : (
                                    <>
                                        <ul>
                                            {alertasMostradas.map((a, idx) => (
                                                <li key={idx} className={`item-alerta ${a.tipo || ""}`}>
                                                    <span className="fecha-alerta"><CalendarDays size={16} /> {a.fecha?.split("T")[0]}</span>
                                                    <span className="sensor-alerta"><Satellite size={16} /> {a.sensor_nombre}</span>
                                                    <span className="tipo-alerta">
                                                        {a.tipo === "alto" ? (
                                                            <><ArrowUpCircle size={16} color="#f44336" /> Alto</>
                                                        ) : (
                                                            <><ArrowDownCircle size={16} color="#2196f3" /> Bajo</>
                                                        )}
                                                    </span>
                                                    <span className="valor-alerta">
                                                        <Hash size={16} /> {a.valor} {obtenerUnidad(a.tipo_sensor, a.sensor_id)}
                                                    </span>
                                                    <span className="limites-alerta">(Rango: {a.valor_minimo} - {a.valor_maximo})</span>
                                                </li>
                                            ))}
                                        </ul>
                                        {alertasOrdenadas.length > 5 && (
                                            <button className="boton-ver-mas" onClick={() => setVerMas(!verMas)}>
                                                {verMas ? (<>Ver menos <ChevronUp size={16} /></>) : (<>Ver más <ChevronDown size={16} /></>)}
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}